<script setup>
import { computed } from 'vue';
import { usePolicy } from 'dashboard/composables/usePolicy';
import Button from 'dashboard/components-next/button/Button.vue';
import BackButton from 'dashboard/components/widgets/BackButton.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Policy from 'dashboard/components/policy.vue';

const props = defineProps({
  currentPage: {
    type: Number,
    default: 1,
  },
  totalCount: {
    type: Number,
    default: 100,
  },
  itemsPerPage: {
    type: Number,
    default: 25,
  },
  headerTitle: {
    type: String,
    default: '',
  },
  backUrl: {
    type: [String, Object],
    default: '',
  },
  buttonPolicy: {
    type: Array,
    default: () => [],
  },
  buttonLabel: {
    type: String,
    default: '',
  },
  isButtonLoading: {
    type: Boolean,
    default: false,
  },
  disableButton: {
    type: Boolean,
    default: false,
  },
  featureFlag: {
    type: String,
    default: '',
  },
  isFetching: {
    type: Boolean,
    default: false,
  },
  showKnowMore: {
    type: Boolean,
    default: true,
  },
  isEmpty: {
    type: Boolean,
    default: false,
  },
  showPaginationFooter: {
    type: Boolean,
    default: true,
  },
  contentMaxWidthClass: {
    type: String,
    default: 'max-w-[60rem]',
  },
});

const emit = defineEmits(['click', 'close', 'update:currentPage']);
const { shouldShowPaywall } = usePolicy();

const showPaywall = computed(() => {
  // 永远不显示付费墙
  return false;
});

const handleButtonClick = () => {
  emit('click');
};

const handlePageChange = event => {
  emit('update:currentPage', event);
};
</script>

<template>
  <section class="flex flex-col w-full h-full overflow-hidden bg-white">
    <header class="sticky top-0 z-20 bg-white border-b border-slate-200">
      <div class="relative overflow-hidden">
        <div class="absolute inset-0 pointer-events-none">
          <div
            class="absolute -top-32 -right-32 w-96 h-96 rounded-full blur-3xl opacity-70 bg-indigo-100"
          />
          <div
            class="absolute inset-0 opacity-60"
            style="
              background-image: radial-gradient(
                rgba(15, 23, 42, 0.06) 1px,
                transparent 1px
              );
              background-size: 22px 22px;
            "
          />
        </div>

        <div class="relative px-6">
          <div class="w-full mx-auto" :class="contentMaxWidthClass">
            <div
              class="flex items-start lg:items-center justify-between w-full py-5 lg:h-20 gap-4 lg:gap-2 flex-col lg:flex-row"
            >
              <div class="flex gap-4 items-center min-w-0">
                <BackButton v-if="backUrl" :to="backUrl" />

                <slot name="headerIcon" />

                <div class="min-w-0">
                  <slot name="headerTitle">
                    <h1 class="text-xl font-semibold text-slate-900 truncate">
                      {{ headerTitle }}
                    </h1>
                  </slot>
                  <slot name="headerSubtitle" />
                </div>

                <div
                  v-if="!isEmpty && showKnowMore"
                  class="hidden sm:flex items-center gap-2 flex-shrink-0"
                >
                  <div class="w-px h-5 rounded-2xl bg-slate-200" />
                  <slot name="knowMore" />
                </div>
              </div>

              <div
                v-if="!showPaywall && buttonLabel"
                v-on-clickaway="() => emit('close')"
                class="relative group/campaign-button flex-shrink-0"
              >
                <Policy :permissions="buttonPolicy">
                  <Button
                    :label="buttonLabel"
                    icon="i-lucide-plus"
                    color="blue"
                    size="sm"
                    class="tm__btn tm__btn--primary"
                    :is-loading="isButtonLoading"
                    :disabled="disableButton || isButtonLoading"
                    @click="handleButtonClick"
                  />
                </Policy>
                <slot name="action" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <main class="flex-1 px-6 overflow-y-auto bg-white">
      <div class="w-full h-full mx-auto py-4" :class="contentMaxWidthClass">
        <slot v-if="!showPaywall" name="controls" />
        <div
          v-if="isFetching"
          class="flex items-center justify-center py-10 text-slate-600"
        >
          <Spinner />
        </div>
        <div v-else-if="showPaywall">
          <slot name="paywall" />
        </div>
        <div v-else-if="isEmpty">
          <slot name="emptyState" />
        </div>
        <slot v-else name="body" />
        <slot />
      </div>
    </main>
    <footer
      v-if="showPaginationFooter"
      class="sticky bottom-0 z-10 px-4 pb-4 bg-white"
    >
      <PaginationFooter
        :current-page="currentPage"
        :total-items="totalCount"
        :items-per-page="itemsPerPage"
        @update:current-page="handlePageChange"
      />
    </footer>
  </section>
</template>
